import React from 'react';

import { useSnackbar } from 'notistack';

import CheckIcon from '@mui/icons-material/Check';
import PublicIcon from '@mui/icons-material/Public';
import { Alert, Button, Card, CardContent, Chip, Divider, Skeleton, Stack, Tooltip, Typography } from '@mui/material';

import { useUpdateBundle } from '~/hooks/apis/bundle/useBundles';

import { Bundle } from '~/types/bundles/bundle';

import { brandName, regionName } from './regions';
import useBundleMarkets from './useBundleMarkets';

type Markets = ReturnType<typeof useBundleMarkets>;

interface Props {
  bundle: Bundle;
}

const LABEL_WIDTH = 170;

function Row({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} alignItems={{ sm: 'center' }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: LABEL_WIDTH }}>
        {label}
      </Typography>
      <Stack direction="row" gap={0.75} flexWrap="wrap">
        {children}
      </Stack>
    </Stack>
  );
}

function CodeRow({ label, codes, nameOf }: { label: string; codes: Array<string>; nameOf: (code: string) => string }) {
  return (
    <Row label={label}>
      {codes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None
        </Typography>
      ) : (
        codes.map((code) => <Chip key={code} size="small" label={nameOf(code)} variant="outlined" />)
      )}
    </Row>
  );
}

interface ChooseRowProps {
  bundle: Bundle;
  field: 'regions' | 'brands';
  label: string;
  noun: string;
  notSellingHint: string;
  narrowing: Markets['regions'];
  selling: Array<string>;
  nameOf: (code: string) => string;
}

/** The shared codes as chips the creator can leave out. Keeping every one saves as not narrowed. */
function ChooseRow({ bundle, field, label, noun, notSellingHint, narrowing, selling, nameOf }: ChooseRowProps) {
  const { enqueueSnackbar } = useSnackbar();
  const updateBundle = useUpdateBundle(bundle.id);
  const { shared, chosen, isNarrowed } = narrowing;

  const save = async (codes: Array<string>) => {
    try {
      await updateBundle.mutateAsync({ [field]: codes.length === shared.length ? [] : codes });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  const toggle = (code: string) => {
    const next = chosen.includes(code)
      ? chosen.filter((kept) => kept !== code)
      : shared.filter((kept) => kept === code || chosen.includes(kept));
    if (next.length > 0) save(next);
  };

  return (
    <Stack spacing={0.5}>
      <Row label={<Typography fontWeight="medium">{label}</Typography>}>
        {shared.map((code) => {
          const isChosen = chosen.includes(code);
          const chip = (
            <Chip
              size="small"
              label={nameOf(code)}
              icon={isChosen ? <CheckIcon /> : undefined}
              color={isChosen ? 'primary' : 'default'}
              variant={isChosen && selling.includes(code) ? 'filled' : 'outlined'}
              onClick={isChosen && chosen.length === 1 ? undefined : () => toggle(code)}
              disabled={updateBundle.isPending}
              aria-pressed={isChosen}
            />
          );
          return isChosen && !selling.includes(code) ? (
            <Tooltip key={code} title={notSellingHint}>
              <span>{chip}</span>
            </Tooltip>
          ) : (
            <React.Fragment key={code}>{chip}</React.Fragment>
          );
        })}
      </Row>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: { sm: `${LABEL_WIDTH + 16}px` } }}>
        <Typography variant="caption" color="text.secondary">
          {isNarrowed
            ? `Narrowed to ${chosen.length} of ${shared.length}.`
            : `Every ${noun} the tour and hotel share. Click one to leave it out.`}
        </Typography>
        {isNarrowed && (
          <Button size="small" onClick={() => save(shared)} disabled={updateBundle.isPending}>
            Keep all
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="overline" color="text.secondary" lineHeight={1}>
        {title}
      </Typography>
      {children}
    </Stack>
  );
}

/** Where the bundle sells: the regions and brands its tour and hotel share, narrowed by the creator. */
export default function BundleMarkets({ bundle }: Props) {
  const markets = useBundleMarkets(bundle);
  const { hasTour, hasHotel, tour, hotel, hasShared, regions, brands, sellsIn, sellsUnder, isLoading, error } = markets;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
          <PublicIcon fontSize="small" color="action" />
          <Typography variant="h6">Where it sells</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          A bundle sells in the regions, and under the brands, where its tour is scheduled and its hotel is on sale. You
          can leave some out, but not add others.
        </Typography>

        {!hasTour || !hasHotel ? (
          <Typography color="text.secondary">Add the tour and the hotel to see where the bundle can sell.</Typography>
        ) : isLoading ? (
          <Stack spacing={1}>
            <Skeleton width="60%" />
            <Skeleton width="50%" />
            <Skeleton width="40%" />
          </Stack>
        ) : error ? (
          <Alert severity="warning">
            The tour or hotel couldn't be loaded, so where the bundle sells isn't known. Reload the page to try again.
          </Alert>
        ) : (
          <Stack spacing={2.5}>
            <Section title="Regions">
              <CodeRow label="Tour scheduled in" codes={tour.regions} nameOf={regionName} />
              <CodeRow label="Hotel on sale in" codes={hotel.regions} nameOf={regionName} />
              {hasShared && (
                <ChooseRow
                  bundle={bundle}
                  field="regions"
                  label="Bundle sells in"
                  noun="region"
                  notSellingHint="Kept, but not on sale under any brand you kept"
                  narrowing={regions}
                  selling={sellsIn}
                  nameOf={regionName}
                />
              )}
            </Section>
            <Divider />
            <Section title="Brands">
              <CodeRow label="Tour sold under" codes={tour.brands} nameOf={brandName} />
              <CodeRow label="Hotel on sale under" codes={hotel.brands} nameOf={brandName} />
              {hasShared && (
                <ChooseRow
                  bundle={bundle}
                  field="brands"
                  label="Bundle sells under"
                  noun="brand"
                  notSellingHint="Kept, but not on sale in any region you kept"
                  narrowing={brands}
                  selling={sellsUnder}
                  nameOf={brandName}
                />
              )}
            </Section>
            {!hasShared ? (
              <Alert severity="error">
                The hotel isn't on sale in any region, under any brand, where the tour is scheduled, so this bundle
                can't be sold. Choose a hotel on sale where the tour is.
              </Alert>
            ) : (
              sellsIn.length === 0 && (
                <Alert severity="error">
                  None of the regions you kept are on sale under a brand you kept. Keep at least one more region or
                  brand.
                </Alert>
              )
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
